import {
  MdPerson,
  MdEdit,
  MdDelete,
  MdVisibility,
  MdSchedule,
  MdBusiness,
  MdLocationOn,
} from 'react-icons/md'
import { Button } from '../ui'

function FlightAttendantCard({ flightAttendant, onView, onEdit, onDelete }) {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success-100 dark:bg-success-900 text-success-600 dark:text-success-400'
      case 'inactive':
        return 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
      case 'on_leave':
        return 'bg-warning-100 dark:bg-warning-900 text-warning-600 dark:text-warning-400'
      case 'terminated':
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
      default:
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString()
  }

  const formatDateOfBirth = (dateString) => {
    if (!dateString) return 'N/A'
    const birthDate = new Date(dateString)
    const today = new Date()
    const age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1
    }
    return age
  }

  const getAvailabilityStatus = (availability) => {
    if (!availability) return 'Unknown'
    const availableDays = Object.values(availability).filter((day) => day).length
    return `${availableDays}/7 days`
  }

  return (
    <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 hover:shadow-lg dark:hover:shadow-lg transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
            <MdPerson className="text-primary-600 dark:text-primary-400 text-xl" />
          </div>
          <div>
            <h3 className="font-semibold text-neutral-900 dark:text-white">
              {flightAttendant.firstName} {flightAttendant.lastName}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {flightAttendant.employeeNumber}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <Button variant="ghost" size="sm" onClick={onView} className="p-1" title="View Details">
            <MdVisibility className="text-lg" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="p-1"
            title="Edit Flight Attendant"
          >
            <MdEdit className="text-lg" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            title="Delete Flight Attendant"
          >
            <MdDelete className="text-lg" />
          </Button>
        </div>
      </div>

      {/* Status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(flightAttendant.status)}`}
          >
            {flightAttendant.status?.charAt(0).toUpperCase() + flightAttendant.status?.slice(1)}
          </span>
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300">
            Age: {formatDateOfBirth(flightAttendant.dateOfBirth)}
          </span>
        </div>
      </div>

      {/* Airline */}
      <div className="flex items-center space-x-2 mb-4">
        <MdBusiness className="text-neutral-500 dark:text-neutral-400" />
        <div className="text-sm">
          <p className="text-neutral-900 dark:text-white font-medium">
            {flightAttendant.airline?.name}
          </p>
          <p className="text-neutral-600 dark:text-neutral-400">{flightAttendant.airline?.code}</p>
        </div>
      </div>

      {/* Base Airport */}
      <div className="flex items-center space-x-2 mb-4">
        <MdLocationOn className="text-neutral-500 dark:text-neutral-400" />
        <div className="text-sm">
          <p className="text-neutral-900 dark:text-white font-medium">
            Base: {flightAttendant.baseAirport}
          </p>
          <p className="text-neutral-600 dark:text-neutral-400">
            Seniority: #{flightAttendant.seniorityNumber}
          </p>
        </div>
      </div>

      {/* Key Information */}
      <div className="space-y-3 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Date of Hire:</span>
          <span className="text-neutral-900 dark:text-white font-medium">
            {formatDate(flightAttendant.dateOfHire)}
          </span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Languages:</span>
          <span className="text-neutral-900 dark:text-white font-medium">
            {flightAttendant.qualifications?.languages?.length || 0}
          </span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Aircraft Types:</span>
          <span className="text-neutral-900 dark:text-white font-medium">
            {flightAttendant.qualifications?.aircraftTypes?.length || 0}
          </span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Availability:</span>
          <span className="text-neutral-900 dark:text-white font-medium">
            {getAvailabilityStatus(flightAttendant.schedule?.availability)}
          </span>
        </div>
      </div>

      {/* Certifications Status */}
      {flightAttendant.certifications && (
        <div className="mb-4">
          <div className="flex justify-between items-center text-sm mb-2">
            <span className="text-neutral-600 dark:text-neutral-400">Next Training:</span>
            <span className="text-neutral-900 dark:text-white font-medium">
              {flightAttendant.certifications.nextRecurrentTraining
                ? formatDate(flightAttendant.certifications.nextRecurrentTraining)
                : 'N/A'}
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {flightAttendant.certifications.nextRecurrentTraining &&
              new Date(flightAttendant.certifications.nextRecurrentTraining) <
                new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && (
                <span className="px-2 py-1 text-xs bg-warning-100 dark:bg-warning-900 text-warning-700 dark:text-warning-300 rounded">
                  Training Due
                </span>
              )}
            {flightAttendant.certifications.nextMedicalTraining &&
              new Date(flightAttendant.certifications.nextMedicalTraining) <
                new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && (
                <span className="px-2 py-1 text-xs bg-warning-100 dark:bg-warning-900 text-warning-700 dark:text-warning-300 rounded">
                  Medical Due
                </span>
              )}
          </div>
        </div>
      )}

      {/* Last Updated */}
      <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400 pt-4 border-t border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center space-x-1">
          <MdSchedule className="text-sm" />
          <span>Updated: {formatDate(flightAttendant.updatedAt)}</span>
        </div>
      </div>
    </div>
  )
}

export default FlightAttendantCard
